import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClients, getMe } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { OutcomeBadge } from '../components/OutcomeBadge';
import type { Client, Outcome } from '../api/types';

const ATTENTION: Outcome[] = ['need_help', 'not_great', 'no_answer'];

export function splitAttention(clients: Client[]): { attention: Client[]; rest: Client[] } {
  const attention: Client[] = [];
  const rest: Client[] = [];
  for (const c of clients) {
    const o = c.lastCheckIn?.outcome;
    if (o && ATTENTION.includes(o)) attention.push(c);
    else rest.push(c);
  }
  return { attention, rest };
}

function ResidentRow({ c }: { c: Client }) {
  return (
    <Link
      to={`/clients/${c.memberId}`}
      className="flex items-center justify-between px-3 py-2.5 no-underline hover:bg-hs-surface-alt"
    >
      <span className="font-medium text-[14px] text-hs-text">{c.displayName}</span>
      <OutcomeBadge outcome={c.lastCheckIn?.outcome ?? null} startedAt={c.lastCheckIn?.completedAt ?? undefined} />
    </Link>
  );
}

export function CommunityDashboardPage() {
  const { getIdToken } = useAuth();
  const [q, setQ] = useState('');
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => getMe(getIdToken) });
  const clientsQuery = useQuery({ queryKey: ['clients'], queryFn: () => getClients(getIdToken) });

  const all = clientsQuery.data?.clients ?? [];
  const filtered = useMemo(
    () => all.filter((c) => c.displayName.toLowerCase().includes(q.trim().toLowerCase())),
    [all, q],
  );
  const { attention, rest } = splitAttention(filtered);

  // Individual accounts belong on the Family view, not the community roster.
  if (meQuery.data && meQuery.data.accountType !== 'community') {
    return <Navigate to="/clients" replace />;
  }
  if (clientsQuery.isLoading) {
    return <p className="text-hs-text-mute">Loading residents…</p>;
  }
  if (clientsQuery.error) {
    return (
      <p role="alert" className="text-hs-danger-text">
        We couldn't load the community roster right now. Please refresh to try again.
      </p>
    );
  }

  return (
    <>
      <PageHeader
        title="Residents"
        subtitle={all.length === 1 ? '1 resident in the community.' : `${all.length} residents in the community.`}
      />

      {all.length === 0 ? (
        <EmptyState title="No residents yet">
          Residents will appear here once they're added to the community.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          <div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search residents"
              aria-label="Search residents"
              className="w-full sm:w-80 border border-hs-border rounded-hs-md px-3 py-1.5 text-[14px] bg-hs-surface text-hs-text placeholder:text-hs-text-mute"
            />
          </div>

          {attention.length > 0 && (
            <section>
              <h2 className="text-[12px] font-bold uppercase tracking-hs-overline text-hs-text-mute mb-2">
                Needs attention
              </h2>
              <div className="rounded-hs-xl border border-hs-border divide-y divide-hs-border bg-hs-surface">
                {attention.map((c) => (
                  <ResidentRow key={c.memberId} c={c} />
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-[12px] font-bold uppercase tracking-hs-overline text-hs-text-mute mb-2">
              All residents ({filtered.length})
            </h2>
            {rest.length === 0 ? (
              <p className="text-[13.5px] text-hs-text-mute">No other residents match your search.</p>
            ) : (
              <div className="rounded-hs-xl border border-hs-border divide-y divide-hs-border bg-hs-surface">
                {rest.map((c) => (
                  <ResidentRow key={c.memberId} c={c} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
